export type IntegrationCredentialTool = 'Semrush' | 'WooCommerce' | 'Magento';

export type IntegrationCredentialValues = {
    apiKey: string;
    storeUrl: string;
    consumerKey: string;
    consumerSecret: string;
    storeDomain: string;
    storeCode: string;
    accessToken: string;
    connectionLabel: string;
};

export type IntegrationCredentialConnection = {
    provider: 'semrush' | 'woocommerce' | 'adobe-commerce';
    connectionConfig: Record<string, string>;
};

export const INTEGRATION_CREDENTIAL_TOOLS = new Set<IntegrationCredentialTool>(['Semrush', 'WooCommerce', 'Magento']);

export const isIntegrationCredentialTool = (tool?: string | null): tool is IntegrationCredentialTool =>
    Boolean(tool) && INTEGRATION_CREDENTIAL_TOOLS.has(tool as IntegrationCredentialTool);

export const createIntegrationCredentialValues = (tool?: string | null): IntegrationCredentialValues => ({
    apiKey: '',
    storeUrl: '',
    consumerKey: '',
    consumerSecret: '',
    storeDomain: '',
    storeCode: tool === 'Magento' ? 'default' : '',
    accessToken: '',
    connectionLabel: '',
});

export const normalizeIntegrationHostname = (value: string): string => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return '';
    }

    try {
        const url = new URL(/^[a-z][a-z\d+.-]*:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`);
        return url.host.toLowerCase();
    } catch {
        return trimmedValue
            .replace(/^[a-z][a-z\d+.-]*:\/\//i, '')
            .split('/')[0]
            .trim()
            .toLowerCase();
    }
};

export const canSubmitIntegrationCredentialForm = (
    tool: IntegrationCredentialTool,
    values: IntegrationCredentialValues,
): boolean => {
    switch (tool) {
        case 'Semrush':
            return Boolean(values.apiKey.trim());
        case 'WooCommerce':
            return Boolean(
                normalizeIntegrationHostname(values.storeUrl) &&
                values.consumerKey.trim() &&
                values.consumerSecret.trim(),
            );
        case 'Magento':
            return Boolean(
                normalizeIntegrationHostname(values.storeDomain) &&
                values.storeCode.trim() &&
                values.accessToken.trim(),
            );
    }
};

export const buildIntegrationCredentialConnection = (
    tool: IntegrationCredentialTool,
    values: IntegrationCredentialValues,
): IntegrationCredentialConnection => {
    switch (tool) {
        case 'Semrush':
            return {
                provider: 'semrush',
                connectionConfig: {
                    apiKey: values.apiKey.trim(),
                },
            };
        case 'WooCommerce':
            return {
                provider: 'woocommerce',
                connectionConfig: {
                    storeURL: normalizeIntegrationHostname(values.storeUrl),
                    consumerKey: values.consumerKey.trim(),
                    consumerSecret: values.consumerSecret.trim(),
                },
            };
        case 'Magento':
            return {
                provider: 'adobe-commerce',
                connectionConfig: {
                    storeDomain: normalizeIntegrationHostname(values.storeDomain),
                    storeCode: values.storeCode.trim(),
                    accessToken: values.accessToken.trim(),
                },
            };
    }
};
